import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faSearch, 
  faCalendarAlt, 
  faUserTie,
  faBuilding,
  faSpinner,
  faArrowLeft,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { liteClient } from 'algoliasearch/lite';
import styles from './SearchManagersModal.module.css';
import defaultProfilePic from '../../assets/Images/defaultUserProfilePicture.png';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';

// Initialize Algolia client
const client = liteClient('6DJNAZSX4P', '342932cbe26f7751ec87fa2b1ff32e4c');

const SearchManagersModal = ({ isOpen, onClose, onSelect, isPastSearch = false, userData }) => {
  const [activeTab, setActiveTab] = useState('Managers');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // For second step - selected entity and dates
  const [selectedEntity, setSelectedEntity] = useState(null);
  
  // For dates - current and past managers
  const [sinceMonth, setSinceMonth] = useState('');
  const [sinceYear, setSinceYear] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [endYear, setEndYear] = useState('');

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    setIsLoading(true);
    try {
      const searchIndex = 'users_index';
      let filters = '';
      
      // Set filter based on active tab
      if (activeTab === 'Managers') {
        filters = 'userType:Manager';
      } else if (activeTab === 'Organizations') {
        filters = 'userType:Organization';
      }

      const { results } = await client.search([{
        indexName: searchIndex,
        query: searchTerm,
        filters,
        hitsPerPage: 10
      }]);

      const mappedResults = results[0].hits.map(hit => ({
        id: hit.objectID,
        fullName: hit.fullName || '',
        userName: hit.userName || '',
        userSubType: hit.userSubType || '',
        profilePictureUrl: hit.profilePictureUrl || '',
        email: hit.email || '',
        idVerificationStatus: hit.idVerificationStatus || '',
        location: [hit.city, hit.state, hit.country].filter(Boolean).join(', '),
        type: hit.userType.toLowerCase() // 'manager' or 'organization'
      }));

      setSearchResults(mappedResults);
      setHasSearched(true);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to handle initial selection
  const handleInitialSelection = (entity) => {
    setSelectedEntity(entity);
    
    // Reset the date fields
    setSinceMonth('');
    setSinceYear('');
    setStartMonth('');
    setStartYear('');
    setEndMonth('');
    setEndYear('');
  };
  
  // Function to go back to search results
  const handleBackToSearch = () => {
    setSelectedEntity(null);
  };
  
  // Function to handle final selection with dates
  const handleFinalSelection = (e) => {
    e.preventDefault();
    
    if (!selectedEntity) return;
    
    // Validate dates based on isPastSearch
    if (isPastSearch) {
      // Validate past fields
      if (!startMonth || !startYear || !endMonth || !endYear) {
        alert('Please fill in all date fields');
        return;
      }
      
      // Validate date logic
      const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1);
      const endDate = new Date(parseInt(endYear), parseInt(endMonth) - 1);
      
      if (startDate > endDate) {
        alert('Start date must be before end date');
        return;
      }
    } else {
      // Validate current fields
      if (!sinceMonth || !sinceYear) {
        alert('Please fill in the since date');
        return;
      }
    }
    
    // Create the enriched entity with date info
    const enrichedEntity = {
      ...selectedEntity
    };
    
    if (isPastSearch) {
      enrichedEntity.duration = {
        startMonth,
        startYear,
        endMonth,
        endYear
      };
    } else {
      enrichedEntity.since = {
        month: sinceMonth,
        year: sinceYear
      };
    }
    
    // Call the parent's onSelect function
    onSelect(enrichedEntity);
  };

  if (!isOpen) return null;
  
  // Generate month options for dropdowns
  const generateMonthOptions = () => {
    return [
      { value: '1', label: 'January' },
      { value: '2', label: 'February' },
      { value: '3', label: 'March' },
      { value: '4', label: 'April' },
      { value: '5', label: 'May' },
      { value: '6', label: 'June' },
      { value: '7', label: 'July' },
      { value: '8', label: 'August' },
      { value: '9', label: 'September' },
      { value: '10', label: 'October' },
      { value: '11', label: 'November' },
      { value: '12', label: 'December' }
    ];
  };

  // Generate year options for dropdowns
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 50; i--) {
      years.push({ value: i.toString(), label: i.toString() });
    }
    return years;
  };

  return (
    <div 
      className={styles.modalOverlay} 
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>
            {selectedEntity 
              ? `Add ${selectedEntity.fullName} as ${isPastSearch ? 'Past ' : ''}Manager` 
              : `Search ${activeTab}`
            }
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {!selectedEntity ? (
          // Search interface
          <>
            <div className={styles.tabs}>
              <button 
                className={`${styles.tab} ${activeTab === 'Managers' ? styles.active : ''}`}
                onClick={() => setActiveTab('Managers')}
              >
                Managers
              </button>
              <button 
                className={`${styles.tab} ${activeTab === 'Organizations' ? styles.active : ''}`}
                onClick={() => setActiveTab('Organizations')}
              >
                Organizations
              </button>
            </div>

            <div className={styles.searchBar}>
              <input
                type="text"
                placeholder={`Search ${activeTab.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button 
                className={styles.searchButton}
                onClick={handleSearch}
                disabled={!searchTerm.trim() || isLoading}
              >
                {isLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faSearch} />
                )}
                Search
              </button>
            </div>

            <div className={styles.searchResults}>
              {isLoading ? (
                <div className={styles.loading}>
                  <div className={styles.spinner}></div>
                  <p>Searching...</p>
                </div>
              ) : hasSearched ? (
                searchResults.length > 0 ? (
                  <div className={styles.resultsList}>
                    {searchResults.map(result => (
                      <div 
                        key={result.id}
                        className={styles.resultCard}
                        onClick={() => handleInitialSelection(result)}
                      >
                        <img 
                          src={result.profilePictureUrl || defaultProfilePic}
                          alt={result.fullName}
                          className={styles.resultPhoto}
                        />
                        <div className={styles.resultInfo}>
                          <div className={styles.nameContainer}>
                            <h3>{result.fullName}</h3>
                            {result.idVerificationStatus === 'Verified' && (
                              <img 
                                src={verifiedBadge} 
                                alt="Verified" 
                                className={styles.verifiedBadge} 
                              />
                            )}
                          </div>
                          <p className={styles.username}>@{result.userName}</p>
                          <p className={styles.subtype}>{result.userSubType}</p>
                          {result.location && <p className={styles.location}>{result.location}</p>}
                          <div className={styles.type}>
                            <FontAwesomeIcon 
                              icon={result.type === 'manager' ? faUserTie : faBuilding} 
                            />
                            <span>
                              {result.type === 'manager' ? 'Manager' : 'Organization'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.noResults}>
                    No {activeTab.toLowerCase()} found for "{searchTerm}"
                  </div>
                )
              ) : (
                <div className={styles.searchPrompt}>
                  Search for {activeTab.toLowerCase()} by name or username
                </div>
              )}
            </div>
          </>
        ) : (
          // Details and date selection for the selected entity
          <form onSubmit={handleFinalSelection} className={styles.detailsForm}>
            <div className={styles.selectedEntityContainer}>
              <div className={styles.selectedEntityHeader}>Selected {selectedEntity.type === 'organization' ? 'Organization' : 'Manager'}:</div>
              <div className={styles.selectedEntityDetails}>
                <img 
                  src={selectedEntity.profilePictureUrl || defaultProfilePic}
                  alt={selectedEntity.fullName}
                  className={styles.resultPhoto}
                />
                <div className={styles.resultInfo}>
                  <div className={styles.nameContainer}>
                    <h3>{selectedEntity.fullName}</h3>
                    {selectedEntity.idVerificationStatus === 'Verified' && (
                      <img 
                        src={verifiedBadge} 
                        alt="Verified" 
                        className={styles.verifiedBadge} 
                      />
                    )}
                  </div>
                  <p className={styles.username}>@{selectedEntity.userName}</p>
                  <p className={styles.subtype}>{selectedEntity.userSubType}</p>
                  {selectedEntity.location && <p className={styles.location}>{selectedEntity.location}</p>}
                </div>
              </div>
            </div>
            
            {/* Date Selection - only render what's appropriate for the current state */}
            <div className={styles.dateSelection}>
              <h3>{isPastSearch ? 'Duration' : 'Since'}</h3>
              
              {isPastSearch ? (
                // Past Relationship - Start and End Dates
                <div className={styles.dateFields}>
                  <div className={styles.dateFieldGroup}>
                    <label className={styles.fieldLabel}>Start Date:</label>
                    <div className={styles.dateInputGroup}>
                      <select 
                        className={styles.dateSelect}
                        value={startMonth}
                        onChange={(e) => setStartMonth(e.target.value)}
                        required
                      >
                        <option value="">Month</option>
                        {generateMonthOptions().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      
                      <select 
                        className={styles.dateSelect}
                        value={startYear}
                        onChange={(e) => setStartYear(e.target.value)}
                        required
                      >
                        <option value="">Year</option>
                        {generateYearOptions().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className={styles.dateFieldGroup}>
                    <label className={styles.fieldLabel}>End Date:</label>
                    <div className={styles.dateInputGroup}>
                      <select 
                        className={styles.dateSelect}
                        value={endMonth}
                        onChange={(e) => setEndMonth(e.target.value)}
                        required
                      >
                        <option value="">Month</option>
                        {generateMonthOptions().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      
                      <select 
                        className={styles.dateSelect}
                        value={endYear}
                        onChange={(e) => setEndYear(e.target.value)}
                        required
                      >
                        <option value="">Year</option>
                        {generateYearOptions().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ) : (
                // Current Relationship - Since Date
                <div className={styles.dateFields}>
                  <div className={styles.dateFieldGroup}>
                    <label className={styles.fieldLabel}>Since:</label>
                    <div className={styles.dateInputGroup}>
                      <select 
                        className={styles.dateSelect}
                        value={sinceMonth}
                        onChange={(e) => setSinceMonth(e.target.value)}
                        required
                      >
                        <option value="">Month</option>
                        {generateMonthOptions().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      
                      <select 
                        className={styles.dateSelect}
                        value={sinceYear}
                        onChange={(e) => setSinceYear(e.target.value)}
                        required
                      >
                        <option value="">Year</option>
                        {generateYearOptions().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className={styles.formActions}>
              <button 
                type="button" 
                className={styles.backButton}
                onClick={handleBackToSearch}
              >
                <FontAwesomeIcon icon={faArrowLeft} /> Back to Search
              </button>
              <button 
                type="submit" 
                className={styles.addButton}
              >
                Add as {isPastSearch ? 'Past ' : ''}Manager
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SearchManagersModal;